import { useState } from 'react'
import { Button, ErrorBanner, Field, Select, TextInput } from '../../components/forms'
import { formatTime } from '../../lib/format'
import { useApprovers, useProductHolds, useReleaseHold, type StockHold } from '../../features/pos/api'
import { SendApprovalCode } from './SendApprovalCode'

/**
 * Who is holding the stock this sale is short of, and giving it to the counter (D-198).
 *
 * **The first job here is explaining, not releasing.** Since D-193 the till holds stock as it goes in
 * a basket, and since D-197 a parked sale holds it for hours — so "only 2 available" with five on the
 * shelf is now an ordinary state rather than a bug. A cashier who can see *"Grace at Counter 2 has
 * three in her basket"* can go and ask her. A cashier who sees only a number concludes the system is
 * broken, which is what this panel exists to prevent.
 *
 * **The second job is the release, and it is a manager's.** It needs their PIN at this till and a
 * reason, exactly like a discount above a cashier's limit — the same situation, a decision at the
 * counter that only a manager may make. Stock somebody has PAID for is never offered here at all.
 */
export function HeldStockPanel({
  locationId,
  registerSessionId,
  productId,
  onReleased,
}: {
  locationId: string
  registerSessionId: string
  productId: string
  /** The hold is gone, so the sale can be quoted again — and now it adds up. */
  onReleased: (hold: StockHold) => void
}) {
  const holds = useProductHolds(locationId, productId, registerSessionId)
  const [asking, setAsking] = useState<StockHold | null>(null)
  const [done, setDone] = useState<StockHold | null>(null)

  const list = holds.data ?? []

  if (done) {
    return (
      <div className="space-y-2 rounded-lg border border-dpl-border bg-dpl-bg px-3 py-2">
        <p className="font-semibold">{done.quantity} given to this sale</p>

        {/* The customer who lost it is owed a telephone call, by a person, now. Said here because
            this is the last moment anybody is looking at the screen that took it from them. */}
        {done.tellTheCustomer && <p>{done.tellTheCustomer}</p>}

        <Button tone="secondary" onClick={() => setDone(null)}>
          Back to the sale
        </Button>
      </div>
    )
  }

  if (asking) {
    return (
      <ReleaseForm
        locationId={locationId}
        hold={asking}
        onCancel={() => setAsking(null)}
        onReleased={(released) => {
          setAsking(null)
          setDone(released)
          onReleased(released)
        }}
      />
    )
  }

  if (holds.isPending || list.length === 0) {
    return null
  }

  return (
    <div>
      <p className="mb-2 font-semibold">Held in this shop</p>

      <ul className="space-y-2">
        {list.map((hold) => (
          <li key={hold.id} className="rounded-lg border border-dpl-border px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="block font-medium">
                  {hold.quantity} · {hold.what}
                </span>
                <span className="block text-xs text-dpl-muted">
                  {hold.who && `${hold.who} · `}
                  held until {formatTime(hold.expiresAt)}
                  {hold.customerName && ` · for ${hold.customerName}`}
                </span>
              </span>

              {hold.mayRelease && (
                <Button tone="secondary" onClick={() => setAsking(hold)}>
                  Give it to this sale
                </Button>
              )}
            </div>

            {/* Not a disabled button with no explanation: why it cannot be taken is the useful part,
                and it usually tells the cashier what to do instead. */}
            {hold.whyNot && <p className="mt-1 text-xs">{hold.whyNot}</p>}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ReleaseForm({
  locationId,
  hold,
  onCancel,
  onReleased,
}: {
  locationId: string
  hold: StockHold
  onCancel: () => void
  onReleased: (hold: StockHold) => void
}) {
  const approvers = useApprovers(locationId, true)
  const release = useReleaseHold(locationId)
  const [approverUserId, setApproverUserId] = useState('')
  const [pin, setPin] = useState('')
  const [reason, setReason] = useState('')

  // Only people who may actually release are offered. Listing a cashier here would produce a refusal
  // and leave somebody at the counter thinking they had been told no by the shop rather than by the
  // software.
  const eligible = (approvers.data ?? []).filter((a) => a.canReleaseHold)
  const chosen = eligible.find((a) => a.id === approverUserId)
  const ready = approverUserId !== '' && pin.length >= 4 && reason.trim().length > 0

  return (
    <form
      className="space-y-3 rounded-lg border border-dpl-border px-3 py-3"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!ready) return

        const released = await release.mutateAsync({
          id: hold.id,
          reason: reason.trim(),
          approverUserId,
          pin,
        })

        setPin('')
        onReleased(released)
      }}
    >
      <p className="text-sm">
        <strong>{hold.quantity} · {hold.what}</strong> goes to this sale.{' '}
        {hold.customerName
          ? `${hold.customerName} will have to be telephoned.`
          : 'Nobody has been promised it by name.'}
      </p>

      <Field label="Released by" htmlFor="hold-approver">
        <Select id="hold-approver" value={approverUserId} onChange={(e) => setApproverUserId(e.target.value)}>
          <option value="">Choose…</option>
          {eligible.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} — {a.role}
            </option>
          ))}
        </Select>
      </Field>

      <SendApprovalCode locationId={locationId} approver={chosen} purpose="HoldRelease" />

      <Field
        label="Their code"
        htmlFor="hold-pin"
        hint="The six digits from their phone — or their PIN, if they have set one."
      >
        <TextInput
          id="hold-pin"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <Field label="Reason" htmlFor="hold-reason" hint="Recorded on the hold and audited.">
        <TextInput
          id="hold-reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Customer here with cash; the parked one has not come back"
        />
      </Field>

      <ErrorBanner error={release.error} />

      <div className="flex gap-2">
        <Button type="submit" busy={release.isPending} disabled={!ready}>
          Release it
        </Button>
        <Button tone="ghost" onClick={onCancel}>
          Leave it held
        </Button>
      </div>
    </form>
  )
}
